import { useAppSelector } from "redux/hooks";
import { PermissionEntries } from "../models/dbModels/permissions/PermissionEntries";
import { PermissionsList } from "../models/dbModels/permissions/PermissionsList";
import { CurrentFreedomLink, FeedbackLink, UserGuideLink } from "./links";
import { RootState } from "redux/store";
import { getFeatureFlagValue, ZFlags } from "components/ZFeatureFlag";
import { FeatureFlagView } from "models/dbModels/featureFlags/FeatureFlag";

export const DEFAULT_USER_PAGE_TITLE = "Dashboard";
export const DEFAULT_USER_PAGE_URL = "/app/dashboards";

export const fullAccess: PermissionEntries = {
    canCreate: true,
    canDelete: true,
    canImport: true,
    canUpdate: true,
    canView: true
};

export const noAccess: PermissionEntries = {
    canCreate: false,
    canDelete: false,
    canImport: false,
    canUpdate: false,
    canView: false
};

export const noAccessPermissionList: PermissionsList = {
    dashboard: noAccess,
    businessDashboard: noAccess,
    operationsDashboard: noAccess,
    managedStock: noAccess,
    departments: noAccess,
    manufacturers: noAccess,
    suppliers: noAccess,
    stockGroups: noAccess,
    priceGroups: noAccess,
    assignPriceGroups: noAccess,
    dispensePricing: noAccess,
    promotions: noAccess,
    preferredBrands: noAccess,
    orders: noAccess,
    supplierDeals: noAccess,
    supplierGroupDeals: noAccess,
    reports: noAccess,
    stores: noAccess,
    accounts: noAccess,
    storeGroups: noAccess,
    lockDownFields: noAccess,
    permissions: noAccess,
    functions: noAccess,
    managedStockPdes: noAccess,
}

export const fullAccessPermissionList: PermissionsList = {
    dashboard: noAccess,
    businessDashboard: noAccess,
    operationsDashboard: noAccess,
    managedStock: noAccess,
    departments: noAccess,
    manufacturers: noAccess,
    suppliers: noAccess,
    stockGroups: noAccess,
    priceGroups: noAccess,
    assignPriceGroups: noAccess,
    dispensePricing: noAccess,
    promotions: noAccess,
    preferredBrands: noAccess,
    orders: noAccess,
    supplierDeals: noAccess,
    supplierGroupDeals: noAccess,
    reports: noAccess,
    stores: noAccess,
    accounts: noAccess,
    storeGroups: noAccess,
    lockDownFields: noAccess,
    permissions: noAccess,
    functions: noAccess,
    managedStockPdes: noAccess,
}

export type MenuItemType = {
    key: string;
    label: string;
    isTitle?: boolean;
    icon?: string;
    url?: string;
    badge?: {
        variant: string;
        text: string;
        hideOnOpen?: boolean;
    };
    parentKey?: string;
    target?: string;
    children?: MenuItemType[];
    isExternal?: boolean;
};

export function useGetMenu(): (includeFeedbackMenu: boolean, userPermissions?: PermissionsList) => MenuItemType[] {
    const global = useAppSelector((state: RootState) => state.global);
    const getMenu = (includeFeedbackMenu: boolean, userPermissions?: PermissionsList) => {
        let permissions: PermissionsList = {
            ...noAccessPermissionList,
            ...userPermissions
        };

        if (!userPermissions?.accounts) permissions.accounts = fullAccess;
        if (!userPermissions?.dashboard) permissions.dashboard = fullAccess;
        if (!userPermissions?.businessDashboard) permissions.businessDashboard = fullAccess;
        if (!userPermissions?.operationsDashboard) permissions.operationsDashboard = fullAccess;
        if (!userPermissions?.departments) permissions.departments = fullAccess;
        if (!userPermissions?.dispensePricing) permissions.dispensePricing = fullAccess;
        if (!userPermissions?.managedStock) permissions.managedStock = fullAccess;
        if (!userPermissions?.manufacturers) permissions.manufacturers = fullAccess;
        if (!userPermissions?.permissions) permissions.permissions = fullAccess;
        if (!userPermissions?.promotions) permissions.promotions = fullAccess;
        if (!userPermissions?.reports) permissions.reports = fullAccess;
        if (!userPermissions?.stockGroups) permissions.stockGroups = fullAccess;
        if (!userPermissions?.storeGroups) permissions.storeGroups = fullAccess;
        if (!userPermissions?.stores) permissions.stores = fullAccess;
        if (!userPermissions?.suppliers) permissions.suppliers = fullAccess;
        if (!userPermissions?.priceGroups) permissions.priceGroups = fullAccess;
        if (!userPermissions?.assignPriceGroups) permissions.assignPriceGroups = fullAccess;
        if (!userPermissions?.preferredBrands) permissions.preferredBrands = fullAccess;
        if (!userPermissions?.functions) permissions.functions = fullAccess;
        if (!userPermissions?.managedStockPdes) permissions.managedStockPdes = fullAccess;
        if (!userPermissions?.lockDownFields) permissions.lockDownFields = fullAccess;



        const result: MenuItemType[] = [];

        //We always display it since we now have a noDashboard page for users who have no permission. Just so they have a landing page
        // const dashboard = {
        //     key: "dashboards",
        //     label: "Dashboards",
        //     isTitle: false,
        //     icon: "uil-home-alt",
        //     children: [
        //         {
        //             key: "business",
        //             label: "Business",
        //             url: "/app/dashboards/business",
        //             parentKey: "dashboards",
        //         },
        //     ]
        // }

        // dashboard.children?.push(
        //     {
        //         key: "operations",
        //         label: "Operations",
        //         url: "/app/dashboards/operations",
        //         parentKey: "dashboards",
        //     }
        // );

        // result.push(dashboard);

        // const stockConflictBadge = (hideOnOpen?: boolean) => global.numStockConflicts > 0 && getFeatureFlagValue(global.featureFlags, "ZFreedom.ManagedStock.StockConflicts") === true ? {
        //     variant: `${global.numStockConflicts > 20 ? "danger" : "warning"}`,
        //     text: `${global.numStockConflicts < 100 ? global.numStockConflicts : "99+"}`,
        //     hideOnOpen: hideOnOpen
        // } : undefined

        // //For now everyone can see stock
        // let stockParent: MenuItemType = {
        //     key: "app-stocks",
        //     label: "Stocks",
        //     isTitle: false,
        //     icon: "uil-box",
        //     badge: stockConflictBadge(true),
        //     children: [],
        // };

        // if (permissions.managedStock.canView) {
        //     stockParent.children?.push({
        //         key: "stocks-managed",
        //         label: "Managed Stocks",
        //         url: "/app/managed",
        //         parentKey: "app-stocks",
        //         badge: stockConflictBadge(),
        //     });
        // }

        // if (permissions.suppliers.canView) {
        //     stockParent.children?.push({
        //         key: "stocks-suppliers",
        //         label: "Suppliers",
        //         url: "/app/suppliers",
        //         parentKey: "app-stocks",
        //     });
        // }

        // if (permissions.departments.canView) {
        //     stockParent.children?.push({
        //         key: "stocks-departments",
        //         label: "Departments",
        //         url: "/app/departments",
        //         parentKey: "app-stocks",
        //     });
        // }

        // if (permissions.manufacturers.canView) {
        //     stockParent.children?.push({
        //         key: "stock-manufacturers",
        //         label: "Manufacturers",
        //         url: "/app/manufacturers",
        //         parentKey: "app-stocks",
        //     });
        // }

        // if (permissions.stockGroups.canView) {
        //     stockParent.children?.push({
        //         key: "stocks-groups",
        //         label: "Stock Groups",
        //         url: "/app/stock-groups",
        //         parentKey: "app-stocks",
        //     });
        // }

        // if (stockParent.children && stockParent.children.length > 0) {
        //     result.push(stockParent);
        // }


        let multiPricingGroup: MenuItemType = {
            key: "app-multi-level-pricing",
            label: "Multi-Level Pricing",
            isTitle: false,
            icon: "mdi mdi-chart-bar",
            children: []
        };

        if (permissions.priceGroups.canView) {
            multiPricingGroup.children?.push({
                key: "multi-level-pricing",
                label: "Price Groups",
                url: "/app/price-groups",
                parentKey: "app-multi-level-pricing",
            });
        }

        if (permissions.assignPriceGroups.canView) {
            multiPricingGroup.children?.push({
                key: "multi-level-pricing-assign",
                label: "Assign Price Group",
                url: "/app/assign-price-group",
                parentKey: "app-multi-level-pricing",
            });
        }

        if (multiPricingGroup.children && multiPricingGroup.children.length > 0) {
            result.push(multiPricingGroup);
        }

        let dispenseParent: MenuItemType = {
            key: "app-dispense",
            label: "Dispense",
            isTitle: false,
            icon: "uil-invoice",
            children: []
        };

        // if (permissions.dispensePricing.canView) {
        //     dispenseParent.children?.push({
        //         key: "managed-dispense-pricing",
        //         label: "Special Pricing",
        //         url: "/app/dispense-pricing",
        //         parentKey: "app-dispense",
        //     });
        // }

        if (permissions.preferredBrands.canView) {
            dispenseParent.children?.push({
                key: "managed-preferred-brands",
                label: "Preferred Brands",
                url: "/app/preferred-brands",
                parentKey: "app-dispense",
            });
        }

        if (dispenseParent.children && dispenseParent.children.length > 0) {
            result.push(dispenseParent);
        }


        // if (permissions.promotions.canView) {
        //     result.push({
        //         key: "app-promotions",
        //         label: "Promotions",
        //         isTitle: false,
        //         icon: " uil-tag-alt",
        //         children: [
        //             {
        //                 key: "managed-promotions",
        //                 label: "Catalogues",
        //                 url: "/app/catalogues",
        //                 parentKey: "app-promotions",
        //             },
        //         ],
        //     });
        // }

        let supplierDealsParent: MenuItemType = {
            key: "app-supplier_deals",
            label: "Supplier Deals",
            isTitle: false,
            icon: "uil-box",
            children: [],
        };

        if (permissions.supplierDeals.canView) {
            supplierDealsParent.children?.push({
                key: "supplier-stock-deals",
                label: "Stock Deals",
                url: "/app/supplier-stock-deals",
                parentKey: "app-supplier_deals",
            });

        }

        if (permissions.supplierGroupDeals.canView) {

            supplierDealsParent.children?.push({
                key: "supplier-assorted-deals",
                label: "Assorted Deals",
                url: "/app/supplier-assorted-deals",
                parentKey: "app-supplier_deals",
            });
        }

        if (supplierDealsParent.children && supplierDealsParent.children.length > 0) {
            result.push(supplierDealsParent);
        }

        if (permissions.reports.canView) {
            result.push({
                key: "app-reports",
                label: "Reports",
                isTitle: false,
                icon: "uil-chart-pie-alt",
                children: [
                    {
                        key: "reports-managed",
                        label: "Store Reports",
                        url: "/app/reports",
                        parentKey: "app-reports",
                    },
                    {
                        key: "reports-till",
                        label: "Till Report",
                        url: "/app/tillReport",
                        parentKey: "app-reports",
                    }
                ],
            });
            result[result.length - 1].children?.push({
                key: "reports-trading-totals",
                label: "Trading Totals Report",
                url: "/app/tradingTotalsReport",
                parentKey: "app-reports",
            });
            if (global.featureFlags != null && getFeatureFlagValue(global.featureFlags, "ZFreedom.Reports.Group.Stock") === true) {
                result[result.length - 1].children?.push({
                    key: "reports-group-stock",
                    label: "Group Stock Report",
                    url: "/app/reports/stock-report",
                    parentKey: "app-reports",
                });
            }
        }

        let storesParent: MenuItemType = {
            key: "app-management",
            label: "Store Management",
            isTitle: false,
            icon: "uil-globe",
            children: []
        };

        // if (permissions.stores.canView) {
        //     storesParent.children?.push(
        //         {
        //             key: "management-stores",
        //             label: "Stores",
        //             url: "/app/stores",
        //             parentKey: "app-management",
        //         })
        // }

        // if (permissions.storeGroups.canView) {
        //     storesParent.children?.push(
        //         {
        //             key: "management-groups",
        //             label: "Store Groups",
        //             url: "/app/groups",
        //             parentKey: "app-management",
        //         });
        // }

        // if (permissions.lockDownFields.canView) {
        //     storesParent.children?.push(
        //         {
        //             key: "management-lockdown",
        //             label: "Lockdown",
        //             url: "/app/storeLockDownFields",
        //             parentKey: "app-management",
        //         });
        // }

        // if (storesParent.children && storesParent.children.length > 0) {
        //     result.push(storesParent);
        // }

        // //For now everyone can see stock
        // let accountParent: MenuItemType = {
        //     key: "app-admin",
        //     label: "Admin",
        //     isTitle: false,
        //     icon: "uil-users-alt",
        //     children: []
        // };

        // if (permissions.accounts.canView) {
        //     accountParent.children?.push({
        //         key: "admin-accounts",
        //         label: "Accounts",
        //         url: "/app/accounts",
        //         parentKey: "app-admin",
        //     });
        // }

        // accountParent.children?.push({
        //     key: "admin-sync",
        //     label: "Sync",
        //     url: "/app/sync",
        //     parentKey: "app-admin",
        // });

        // if (accountParent.children && accountParent.children.length > 0) {
        //     result.push(accountParent);
        // }

        result.push({
            key: "user-guide",
            label: "User Guide",
            icon: "uil-question-circle",
            url: UserGuideLink,
            isExternal: true,
            target: "_blank"
        });

        result.push({
            key: "back-to-freedom",
            label: "Back to New Design",
            icon: "mdi mdi-arrow-left-bold-box-outline",
            url: CurrentFreedomLink,
            isExternal: true,
        })

        // if (includeFeedbackMenu) {
        //     result.push({
        //         key: "feedback",
        //         label: "Leave Feedback",
        //         icon: "mdi mdi-comment-quote-outline",
        //         url: FeedbackLink,
        //         isExternal: true,
        //         target: "_blank"
        //     });
        // }

        return result;
    }

    return getMenu;
}